import { useState, useCallback, useEffect, useRef } from 'react';
import { FiVideo } from 'react-icons/fi';
import VideoTreeSidebar from './VideoTreeSidebar';
import { getVideos, getVideoFilterTreeByCategory } from '../api/publicApi';
import { fetchVideoByIdDirect, fetchVideoById } from '../services/videoService';
import { VideoPreview } from './VideoPlayers';
import { SkeletonBox } from './Skeleton';

function fmtDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Generic video grid page with persistent preview player.
 * Displays sidebar filter tree, large preview player at top, and all videos in a grid below.
 *
 * Props:
 *  - section {string}  – backend section enum value (e.g. 'prieres')
 *  - sidebarLabel {string} – label shown in the sidebar section header
 *  - renderKey {'byYear'|'byTheme'} – tree render mode (default: 'byYear')
 *  - videoTitlePrefix {string|null} – optional text prefix before the date in the player title
 *  - showThemeFilter {boolean} – whether to show the theme filter (default: false)
 *  - multipleVideosPerDay {boolean} – whether to allow expanding days to see multiple videos
 *  - extraSections {Array|null} – override the default sidebar sections array
 */
export default function VideoGridPage({
  section,
  sidebarLabel,
  renderKey = 'byYear',
  videoTitlePrefix = null,
  showThemeFilter = false,
  multipleVideosPerDay = false,
  extraSections = null,
}) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [filters, setFilters] = useState({ type: '', annee: '', mois: '', semaine: '', theme: '', dateFrom: '', dateTo: '' });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterTree, setFilterTree] = useState({ years: [], themes: [], sections: [] });
  const [treeLoading, setTreeLoading] = useState(true);
  const [filterRunId, setFilterRunId] = useState(0);
  const [page, setPage] = useState(1);
  const [allVideos, setAllVideos] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [loading, setLoading] = useState(false);
  const isDayBased = section === 'motivation_lundi';
  const limit = 100;
  const filterKeyRef = useRef('');

  // Load filter tree on mount
  useEffect(() => {
    const fetchTree = async () => {
      setTreeLoading(true);
      try {
        const sectionRes = await getVideoFilterTreeByCategory(section);
        let treeData = sectionRes?.data?.data || { years: [], themes: [], sections: [] };
        setFilterTree(treeData);
      } catch (error) {
        console.error('Error fetching video tree:', error);
        setFilterTree({ years: [], themes: [], sections: [] });
      } finally {
        setTreeLoading(false);
      }
    };
    fetchTree();
  }, [section]);

  // Fetch videos when filters change
  useEffect(() => {
    const filterKey = JSON.stringify({ section, appliedFilters });
    const isNewFilter = filterKey !== filterKeyRef.current;

    let cancelled = false;
    setLoading(true);

    const params = {
      section,
      statut: 'actif',
      limit,
      page,
      ...(appliedFilters.annee && { year: appliedFilters.annee }),
      ...(appliedFilters.mois && { month: appliedFilters.mois }),
      ...(appliedFilters.semaine && { week: appliedFilters.semaine }),
      ...(appliedFilters.jour && { day: appliedFilters.jour }),
      ...(appliedFilters.theme && { theme: appliedFilters.theme }),
      ...(appliedFilters.dateFrom && { dateFrom: appliedFilters.dateFrom }),
      ...(appliedFilters.dateTo && { dateTo: appliedFilters.dateTo }),
    };

    getVideos(params).then(res => {
      if (cancelled) return;
      const incoming = res.data?.data || [];
      const pagination = res.data?.pagination || {};
      
      if (isNewFilter) {
        filterKeyRef.current = filterKey;
        setAllVideos(incoming);
        // NO auto-select on new filter - user must click to play
      } else {
        setAllVideos(prev => [...prev, ...incoming]);
      }
      setPaginationData(pagination);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, section, filterRunId]);

  const sections = extraSections ?? [{ key: `by_${section}`, label: sidebarLabel, renderKey }];

  const handleClear = useCallback(() => {
    const emptyFilters = { type: '', annee: '', mois: '', semaine: '', theme: '', dateFrom: '', dateTo: '' };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    filterKeyRef.current = '';
    setFilterRunId(prev => prev + 1);
    setAllVideos([]);
    setPage(1);
    setSelectedVideo(null);
  }, []);

  const hasActiveFilters = Object.values(filters).some(val => val !== '');

  // Sidebar filter click: applies filters without auto-selecting video
  const handleSidebarFilter = async (newFilters) => {
    const { _video, _videoId, ...filterOnly } = newFilters;

    // Check if this is a video selection from sidebar tree
    if (_video || _videoId) {
      setVideoLoading(true);
      setShowPreview(true);
      
      try {
        const videoId = _videoId || _video?.id || _video?._id;
        const fullVideo = await fetchVideoByIdDirect(videoId);
        setSelectedVideo(fullVideo);
        setVideoLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } catch (directError) {
        try {
          const filterParams = {
            section: section,
            statut: 'actif',
            limit: 100,
            page: 1,
          };
          
          if (newFilters.annee) {
            filterParams.year = newFilters.annee;
          }
          if (newFilters.mois) {
            filterParams.month = newFilters.mois;
          }
          
          const videoId = _videoId || _video?.id || _video?._id;
          const fullVideo = await fetchVideoById(videoId, filterParams);
          setSelectedVideo(fullVideo);
          setVideoLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } catch (listError) {
          setSelectedVideo(_video);
          setVideoLoading(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
      }
    }

    // Just apply the filter without playing any video
    const updatedFilters = { ...filters, ...filterOnly };

    if (filterOnly.jour) {
      updatedFilters.jour = filterOnly.jour;
      updatedFilters.annee = filterOnly.annee || '';
      updatedFilters.mois = filterOnly.mois || '';
      updatedFilters.semaine = '';
    } else if (filterOnly.dateFrom || filterOnly.dateTo) {
      updatedFilters.annee = '';
      updatedFilters.mois = '';
      updatedFilters.semaine = '';
      updatedFilters.jour = '';
      updatedFilters.dateFrom = filterOnly.dateFrom || '';
      updatedFilters.dateTo = filterOnly.dateTo || '';
    } else {
      updatedFilters.dateFrom = '';
      updatedFilters.dateTo = '';
      updatedFilters.jour = '';
    }

    if (filterOnly.annee || filterOnly.mois || filterOnly.theme || filterOnly.semaine) {
      updatedFilters.dateFrom = '';
      updatedFilters.dateTo = '';
      updatedFilters.jour = '';
    }

    filterKeyRef.current = '';
    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);
    setFilterRunId(prev => prev + 1);
    setAllVideos([]);
    setPage(1);
    // DO NOT select a video - let user choose from the grid
    setShowPreview(false);
  };

  // Grid video click: show preview page with video player
  const handleGridVideoSelect = async (video) => {
    try {
      setVideoLoading(true);
      setShowPreview(true);
      const videoId = video.id || video._id;
      const fullVideo = await fetchVideoByIdDirect(videoId);
      setSelectedVideo(fullVideo);
      setVideoLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (directError) {
      try {
        const fullVideo = await fetchVideoById(video.id || video._id, {
          section: section,
          statut: 'actif',
          limit: 100,
          page: 1,
        });
        setSelectedVideo(fullVideo);
        setVideoLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (fallbackError) {
        setSelectedVideo(video);
        setVideoLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const fetchThemeData = useCallback(async (theme) => {
    try {
      const res = await getVideoFilterTreeByCategory(theme);
      const data = res.data?.data || { years: [] };
      return data;
    } catch (err) {
      return { years: [] };
    }
  }, []);

  const videoTitle = selectedVideo
    ? videoTitlePrefix
      ? videoTitlePrefix + (fmtDate(selectedVideo.date).charAt(0).toUpperCase() + fmtDate(selectedVideo.date).slice(1))
      : fmtDate(selectedVideo.date).charAt(0).toUpperCase() + fmtDate(selectedVideo.date).slice(1)
    : '';

  // Get video thumbnail image
  const getVideoThumbnail = (video) => {
    if (video.image) return video.image;
    if (video.thumbnail) return video.thumbnail;
    if (video.imageUrl) return video.imageUrl;
    // Fallback: try to extract YouTube thumbnail
    if (video.lien_primaire && video.lien_primaire.includes('youtube')) {
      const videoId = video.lien_primaire.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1];
      if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }
    return null;
  };

  return (
    <div className="two-col">
      <VideoTreeSidebar
        sections={sections}
        filterTree={filterTree}
        onFilterSelect={handleSidebarFilter}
        onFetchThemeData={fetchThemeData}
        onClearFilters={handleClear}
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        selectedVideo={selectedVideo}
        multipleVideosPerDay={multipleVideosPerDay}
        isDayBased={isDayBased}
        className="video-sidebar"
      />

      <div className="main-col pt-3">
        {/* Show preview player page */}
        {showPreview && selectedVideo ? (
          <>
            {/* Back link */}
            <div
              onClick={() => setShowPreview(false)}
              style={{
                marginBottom: '24px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: '#8b1512',
                fontWeight: 600,
                fontSize: '14px',
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              ← Retour à la liste
            </div>

            {/* Preview Player Section */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ maxWidth: '600px', width: '100%', margin: '0 auto' }}>
                {selectedVideo.theme && (
                  <p className="video-title" style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px', lineHeight: 1.3 }}>
                    {selectedVideo.theme}
                  </p>
                )}
                {videoTitle && (
                  <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>{videoTitle}</p>
                )}
                <VideoPreview video={selectedVideo} loading={videoLoading} />
              </div>
            </div>
          </>
        ) : (
          // Show grid page
          <>
            {/* Loading state */}
            {(treeLoading || (loading && allVideos.length === 0 && !selectedVideo)) ? (
              <div style={{ paddingTop: 8 }}>
                <SkeletonBox width="55%" height={18} borderRadius={4} style={{ marginBottom: 12 }} />
                <SkeletonBox width="30%" height={13} borderRadius={4} style={{ marginBottom: 20 }} />
                <SkeletonBox height={340} borderRadius={12} style={{ marginBottom: 16 }} />
                <SkeletonBox width="70%" height={13} borderRadius={4} style={{ marginBottom: 8 }} />
                <SkeletonBox width="45%" height={13} borderRadius={4} />
              </div>
            ) : allVideos.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
                <FiVideo size={36} style={{ display: 'block', margin: '0 auto 12px' }} />
                <p>Aucune vidéo trouvée pour ces critères.</p>
              </div>
            ) : (
              <>
                {/* Video Grid */}
                <div className="video-grid">
                  {allVideos.map((video) => {
                    const thumbnail = getVideoThumbnail(video);
                    const displayTitle = video.theme || video.titre || video.title || 'Vidéo';
                    const displayDate = fmtDate(video.date);
                    return (
                      <div
                        key={video._id || video.id}
                        className="video-card"
                        onClick={() => handleGridVideoSelect(video)}
                        style={{ cursor: 'pointer' }}>
                        <div className="video-card__thumb">
                          {thumbnail ? (
                            <img
                              src={thumbnail}
                              alt={displayTitle}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <FiVideo size={32} color="#999" />
                            </div>
                          )}
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                          }}>
                            <span style={{ marginLeft: '4px', color: '#d32f2f' }}>▶</span>
                          </div>
                        </div>
                        <div className="video-card__info">
                          <h3 className="video-card__title">{displayTitle}</h3>
                          {displayDate && <p className="video-card__date">{displayDate}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Load more button if pagination available */}
                {page < (paginationData.totalPages || 1) && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '40px' }}>
                    <button
                      className="btn-mcm outline"
                      disabled={loading}
                      onClick={() => setPage(p => p + 1)}
                    >
                      {loading ? 'Chargement…' : 'Charger plus de vidéos'}
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
